"use client";

import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import Autocomplete from "@/components/ui/autocomplete";
import { api } from "@/lib/trpc/client";
import crypto from "crypto";
import { ParentSize } from "@visx/responsive";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart4, PieChart } from "lucide-react";
import { PieChartTable } from "./PieChartTable";
import BudgetPieChart from "./BudgetPieChart";
import { useTranslations } from "use-intl";
import { Icon } from "@iconify/react";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
const maxYear = (() => {
  const currentMonth = new Date().getMonth();
  return currentMonth < 2 ? new Date().getFullYear() - 1 : new Date().getFullYear();
})();
const startingYear = 2016;
const availableYears = Array.from({
  length: maxYear - startingYear + 1
}, (_, i) => (maxYear - i).toString());
const generateColorFromLabel = (label: string) => {
  const hash = crypto.createHash("md5").update(label).digest("hex");
  return `#${hash.substr(0, 6)}`;
};
const options = {
  responsive: true,
  plugins: {
    legend: {
      position: "right"
    },
    title: {
      display: false
    }
  },
  scales: {
    x: {
      stacked: true
    },
    y: {
      stacked: true
    }
  }
} as const;
export function AnnualBudgetExecutionCard({
  institutionId
}: {
  institutionId: string;
}) {
  const t = useTranslations();
  const [selectedYear, setSelectedYear] = React.useState<string>(maxYear.toString());
  const [selectedType, setSelectedType] = React.useState<"venituri" | "cheltuieli">("venituri");
  const [selectedView, setSelectedView] = React.useState<"pie" | "bar">("pie");
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null);
  const budgetData = api.budgetPublicRouter.getAllTempBudgetData.useQuery({
    institutionId
  });
  const data = budgetData.data?.find(d => (d.year ?? 0) === parseInt(selectedYear))?.data || null;
  const barData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.luni,
      datasets: Object.entries(data[selectedType].diagrama).map(([label, values]) => ({
        label,
        data: values,
        backgroundColor: generateColorFromLabel(label)
      }))
    };
  }, [data, selectedType]);
  const categories = (selectedType === "cheltuieli" ? data?.cheltuieli?.tabel : data?.venituri?.tabel) || [];
  const visibleCategories = selectedCategory ? categories.filter(category => category.desc === selectedCategory) : categories;
  const visibleSubcategories = categories.find(category => category.desc === selectedCategory)?.linii || [];
  React.useEffect(() => {
    setSelectedCategory(null);
  }, [selectedYear]);
  if (budgetData.isLoading) {
    return <Card className="flex h-full items-center justify-center p-8">
        <Icon icon="svg-spinners:pulse-rings-multiple" width="48" height="48" />
      </Card>;
  }
  return <Card className="h-full rounded-3xl pb-8" data-sentry-element="Card" data-sentry-component="AnnualBudgetExecutionCard" data-sentry-source-file="AnnualBudgetExecutionCard.tsx">
      <div className="flex flex-col items-center justify-between gap-2 space-y-1.5 rounded-t-lg p-6 desktop:flex-row">
        <div>
          <h3 className="text-2xl font-semibold leading-none tracking-tight text-glow">
            {t("annual_budget_execution", {
            type: selectedType
          })}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("your_annual_budget_execution_subtitle")}
          </p>
        </div>
        <div className="flex w-full max-w-[260px] items-center justify-center gap-3 pr-8">
          <Button onClick={() => setSelectedView(selectedView === "pie" ? "bar" : "pie")} className="hidden h-12 rounded-3xl px-6 desktop:block" data-sentry-element="Button" data-sentry-source-file="AnnualBudgetExecutionCard.tsx">
            {selectedView === "pie" ? <PieChart size={24} /> : <BarChart4 size={24} />}
          </Button>
          <Autocomplete placeholder={selectedYear || "An"} options={availableYears.map(year => ({
          label: year,
          value: year
        }))} initialValue={selectedYear} onChangeAction={value => setSelectedYear(String(value.value))} width={200} data-sentry-element="Autocomplete" data-sentry-source-file="AnnualBudgetExecutionCard.tsx" />
          <Autocomplete placeholder={selectedType || "Type"} options={[{
          label: "Venituri",
          value: "venituri"
        }, {
          label: "Cheltuieli",
          value: "cheltuieli"
        }]} initialValue={selectedType} onChangeAction={value => setSelectedType(String(value.value) as "venituri" | "cheltuieli")} width={200} data-sentry-element="Autocomplete" data-sentry-source-file="AnnualBudgetExecutionCard.tsx" />
        </div>
      </div>
      <div className="hidden desktop:block" key={`${selectedYear}-${selectedType}-${selectedView}`}>
        {selectedView === "pie" ? <ParentSize>
            {parent => <div className="flex w-full items-center px-8 pr-24" style={{
          width: parent.width,
          height: 800
        }}>
                <BudgetPieChart width={Math.min(800, parent.width / 3 * 2)} height={Math.min(800, parent.width / 2)} categories={visibleCategories.map(category => ({
            label: category.desc,
            value: category.suma
          }))} subcategories={visibleSubcategories.map(subcategory => ({
            label: subcategory.desc,
            value: subcategory.suma
          }))} onCategoryClick={category => {
            const isSubcategory = visibleSubcategories.some(subcategory => subcategory.desc === category);
            if (isSubcategory) return;
            setSelectedCategory(selectedCategory ? null : category);
          }} />
                <PieChartTable {...{
            selectedCategory,
            visibleCategories,
            visibleSubcategories
          }} />
              </div>}
          </ParentSize> : barData ? <ParentSize>
            {parent => <div style={{
          width: parent.width,
          height: 800
        }} className="p-12">
                <Bar data={barData} options={options} height={800} width={parent.width} />
              </div>}
          </ParentSize> : null}
      </div>
      <div className="px-4 desktop:hidden">
        <PieChartTable {...{
        selectedCategory: null,
        visibleCategories,
        visibleSubcategories
      }} allowOverflow data-sentry-element="PieChartTable" data-sentry-source-file="AnnualBudgetExecutionCard.tsx" />
      </div>
    </Card>;
}